import { Box, Tooltip, IconButton } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useChatRail } from "./ChatRailProvider";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";
import { paths } from "../../../routes/paths";
import type { AgentTab } from "../hooks/useAgentTabs";

function AgentIconBubble({
  tab,
  selected,
  onClick,
}: {
  tab: AgentTab;
  selected: boolean;
  onClick: () => void;
}) {
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  return (
    <Tooltip title={tab.agent.name} placement="left">
      <Box sx={{ position: "relative", width: T.tab_size, height: T.tab_size + 6 }}>
        <Box
          component="button"
          type="button"
          onClick={onClick}
          aria-pressed={selected}
          sx={{
            // Style E tab anatomy (parts-tabstrip.jsx:163-185): squircle
            // avatar, panel2 active fill (no border), accent dot beneath
            // for the active state. Hover lifts inactive tabs to panel2.
            width: T.tab_size,
            height: T.tab_size,
            borderRadius: `${T.r_md}px`,
            border: "1px solid transparent",
            bgcolor: selected ? T.panel2 : "transparent",
            color: selected ? T.text : T.textDim,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: T.fs_h,
            fontWeight: 600,
            fontFamily: T.sans,
            transition: "background-color 120ms ease",
            "&:hover": { bgcolor: T.panel2 },
          }}
        >
          {tab.agent.icon ? (
            <Box component="span" sx={{ lineHeight: 1 }}>
              {tab.agent.icon}
            </Box>
          ) : (
            <SmartToyIcon fontSize="small" />
          )}
        </Box>
        {selected && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: 4,
              height: 4,
              borderRadius: "50%",
              bgcolor: T.accent,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

export function AgentTabStrip() {
  const { agentTabs, activeAgentId, setActiveAgentId } = useChatRail();
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const navigate = useNavigate();

  const personal = agentTabs.filter((t) => t.section === "personal");
  const shared = agentTabs.filter((t) => t.section === "shared");

  return (
    <Box
      role="group"
      aria-label="Agent tabs"
      sx={{
        width: T.tab_strip_w,
        bgcolor: T.panel,
        borderRight: `1px solid ${T.hair}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 1,
        gap: 0.5,
        flexShrink: 0,
        overflowY: "auto",
      }}
    >
      {personal.map((tab) => (
        <AgentIconBubble
          key={tab.agent.id}
          tab={tab}
          selected={tab.agent.id === activeAgentId}
          onClick={() => setActiveAgentId(tab.agent.id)}
        />
      ))}

      {personal.length > 0 && shared.length > 0 && (
        <Box sx={{ width: 24, height: 1, bgcolor: T.hair2, my: 0.5 }} />
      )}

      {shared.map((tab) => (
        <AgentIconBubble
          key={tab.agent.id}
          tab={tab}
          selected={tab.agent.id === activeAgentId}
          onClick={() => setActiveAgentId(tab.agent.id)}
        />
      ))}

      <Box sx={{ flex: 1 }} />

      <Tooltip title="Manage agents" placement="left">
        <IconButton size="small" onClick={() => navigate(paths.agents)}>
          <AddIcon fontSize="small" sx={{ color: T.textMute }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
